// aifon-api client — Bearer token ile read endpoint'leri çağırır.
// Ayar persistence: localStorage. UI ilk açılışta token istemek için
// `loadConfig()` kullanır; "Ayarlar" panelinden değiştirilebilir.

const LS_API_BASE = "aifon:api_base";
const LS_TOKEN = "aifon:read_token";

// Vite define ile inject ediliyor (vite.config.ts).
declare const __API_BASE_DEFAULT__: string;

export interface ApiConfig {
  base: string;
  token: string;
}

export function loadConfig(): ApiConfig {
  const base = localStorage.getItem(LS_API_BASE) ?? __API_BASE_DEFAULT__ ?? "";
  const token = localStorage.getItem(LS_TOKEN) ?? "";
  return { base, token };
}

export function saveConfig(cfg: ApiConfig): void {
  localStorage.setItem(LS_API_BASE, cfg.base);
  localStorage.setItem(LS_TOKEN, cfg.token);
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiFetch<T>(path: string, cfg: ApiConfig, init: RequestInit = {}): Promise<T> {
  if (!cfg.base) {
    throw new ApiError("API base ayarlanmamış. Ayarlar'dan gir.", 0);
  }
  const url = cfg.base.replace(/\/$/, "") + path;
  const headers = new Headers(init.headers);
  if (cfg.token) headers.set("Authorization", `Bearer ${cfg.token}`);
  headers.set("Accept", "application/json");

  const resp = await fetch(url, { ...init, headers });
  const text = await resp.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!resp.ok) {
    const msg =
      (body && typeof body === "object" && "error" in body && typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : `HTTP ${resp.status}`);
    throw new ApiError(msg, resp.status, body);
  }
  return body as T;
}

import type {
  Anomaly,
  GoalCurrent,
  MotherLedger,
  OtherAssetsListResponse,
  PortfolioCombined,
  PropertiesListResponse,
  QueuedSignal,
  SignalStatus,
  SpouseLotCreateInput,
  SpouseLotCreateResponse,
  SpouseLotDeleteResponse,
  SpouseLotsResponse,
  SpouseSummary,
  TranslatorEvent,
} from "../types/api";

// ----- response tipleri (aifon-api ile senkron) ----------------------------

export interface PortfolioSummary {
  ok: true;
  ts: string;
  open_lot_count: number;
  closed_lot_count: number;
  fund_count: number;
  total_cost_tl: number;
  total_value_tl: number;
  unrealized_pnl_tl: number;
  unrealized_pnl_pct: number;
  realized_pnl_tl: number;
  realized_tax_tl: number;
  mother_value_tl: number;
  mother_share: number;
  last_price_date: string | null;
  last_lot_update: string | null;
  funds_missing_nav: string[];
}

export interface HealthResponse {
  ok: boolean;
  service?: string;
  version?: string;
  ts?: string;
  table_count?: number;
  lot_sales_count?: number;
  mother_subaccount?: string;
  known_self_count?: number;
}

// ----- read endpoints -------------------------------------------------------

export function getHealth(cfg: ApiConfig): Promise<HealthResponse> {
  return apiFetch<HealthResponse>("/healthz", cfg);
}

export function getPortfolioSummary(cfg: ApiConfig): Promise<PortfolioSummary> {
  return apiFetch<PortfolioSummary>("/portfolio/summary", cfg);
}

// ----- anomalies ------------------------------------------------------------

export interface AnomalyListResponse {
  ok: true;
  ts: string;
  count: number;
  items: Anomaly[];
}

export interface AnomalyResolveResponse {
  ok: true;
  id: string;
  resolved_at: string;
  already_resolved: boolean;
}

export function getAnomaliesOpen(cfg: ApiConfig): Promise<AnomalyListResponse> {
  return apiFetch<AnomalyListResponse>("/anomalies/open", cfg);
}

export function getAnomaliesHistory(
  cfg: ApiConfig,
  limit = 14
): Promise<AnomalyListResponse> {
  return apiFetch<AnomalyListResponse>(`/anomalies/history?limit=${limit}`, cfg);
}

export function resolveAnomaly(
  cfg: ApiConfig,
  id: string | number
): Promise<AnomalyResolveResponse> {
  return apiFetch<AnomalyResolveResponse>(`/anomalies/${id}/resolve`, cfg, {
    method: "POST",
  });
}

// ----- signals --------------------------------------------------------------

export interface SignalsRecentResponse {
  ok: true;
  ts: string;
  days: number;
  count: number;
  items: QueuedSignal[];
}

export interface SignalStatusResponse {
  ok: true;
  id: string;
  status: SignalStatus;
  set_at: string;
}

export function getSignalsRecent(
  cfg: ApiConfig,
  opts: { days?: number; includeLowScore?: boolean } = {}
): Promise<SignalsRecentResponse> {
  const days = opts.days ?? 7;
  const includeLow = opts.includeLowScore ?? true;
  const q = new URLSearchParams({
    days: String(days),
    include_low_score: String(includeLow),
  });
  return apiFetch<SignalsRecentResponse>(`/signals/recent?${q}`, cfg);
}

export function setSignalStatus(
  cfg: ApiConfig,
  id: string | number,
  status: SignalStatus
): Promise<SignalStatusResponse> {
  return apiFetch<SignalStatusResponse>(`/signals/${id}/status`, cfg, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ status }),
  });
}

// ----- mother ledger --------------------------------------------------------

export type MotherLedgerResponse = MotherLedger & { ok: true; ts: string };

export function getMotherLedger(
  cfg: ApiConfig,
  month?: string
): Promise<MotherLedgerResponse> {
  const path = month ? `/mother/ledger?month=${month}` : "/mother/ledger";
  return apiFetch<MotherLedgerResponse>(path, cfg);
}

// ----- goal -----------------------------------------------------------------

export function getGoalCurrent(cfg: ApiConfig): Promise<GoalCurrent> {
  return apiFetch<GoalCurrent>("/goal/current", cfg);
}

// ----- translator (Çevirmen Şeridi) -----------------------------------------

export interface TranslatorTodayResponse {
  ok: true;
  ts: string;
  today: string;
  count: number;
  items: TranslatorEvent[];
}

export function getTranslatorToday(cfg: ApiConfig): Promise<TranslatorTodayResponse> {
  return apiFetch<TranslatorTodayResponse>("/translator/today", cfg);
}

// ----- spouse portfolio (Task 11) -------------------------------------------

export function getSpouseSummary(cfg: ApiConfig): Promise<SpouseSummary> {
  return apiFetch<SpouseSummary>("/spouse/summary", cfg);
}

export function getSpouseLots(
  cfg: ApiConfig,
  opts: { includeClosed?: boolean } = {},
): Promise<SpouseLotsResponse> {
  const path = opts.includeClosed
    ? "/spouse/lots?include_closed=true"
    : "/spouse/lots";
  return apiFetch<SpouseLotsResponse>(path, cfg);
}

export function createSpouseLot(
  cfg: ApiConfig,
  input: SpouseLotCreateInput,
): Promise<SpouseLotCreateResponse> {
  return apiFetch<SpouseLotCreateResponse>("/spouse/lots", cfg, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ lot: input }),
  });
}

export function deleteSpouseLot(
  cfg: ApiConfig,
  id: number | string,
): Promise<SpouseLotDeleteResponse> {
  return apiFetch<SpouseLotDeleteResponse>(`/spouse/lots/${id}`, cfg, {
    method: "DELETE",
  });
}

// ----- portfolio combined ---------------------------------------------------

export function getPortfolioCombined(cfg: ApiConfig): Promise<PortfolioCombined> {
  return apiFetch<PortfolioCombined>("/portfolio/combined", cfg);
}

// ----- properties (Task 13) -------------------------------------------------

export function getPropertiesList(cfg: ApiConfig): Promise<PropertiesListResponse> {
  return apiFetch<PropertiesListResponse>("/properties/list", cfg);
}

export interface PropertyOverrideInput {
  value_tl: number;
  period: string; // 'YYYY-MM'
  source: "emlakci" | "kullanici_tahmini" | "satis";
  note?: string;
}

export function postPropertyOverride(
  cfg: ApiConfig,
  id: number,
  input: PropertyOverrideInput,
): Promise<unknown> {
  return apiFetch<unknown>(`/properties/${id}/override`, cfg, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

// ----- other assets (Task 15) -----------------------------------------------

export function getOtherAssetsList(cfg: ApiConfig): Promise<OtherAssetsListResponse> {
  return apiFetch<OtherAssetsListResponse>("/other-assets/list", cfg);
}

export interface OtherAssetOverrideInput {
  value_tl: number;
  period: string; // 'YYYY-MM'
  source: "kurum_raporu" | "kullanici_tahmini" | "satis_teklifi" | "satis";
  note?: string;
}

export function postOtherAssetOverride(
  cfg: ApiConfig,
  id: number,
  input: OtherAssetOverrideInput,
): Promise<unknown> {
  return apiFetch<unknown>(`/other-assets/${id}/override`, cfg, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}
